import type { FunctionComponent } from 'react';
import { Fragment, useMemo } from 'react';
import { getAlpha2Code, getName } from 'i18n-iso-countries';
import { CalendarIcon } from 'lucide-react';
import { CircleFlag } from 'react-circle-flags';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import type { DisplayableMetricType } from '../types';
import { Card, CardContent, CardHeader } from '@/components';
import { CardDescription } from '@/components/atoms';
import { Separator } from '@/components/atoms/Separator';
import { GEO_URL } from '../constants';
import { getBlueShade } from '../utils';

export const VisitorsCountryMap: FunctionComponent<{
  visitorsCountries: DisplayableMetricType[];
  visitorsCountriesDateRange: string;
}> = ({ visitorsCountries, visitorsCountriesDateRange }) => {
  const maxShare = useMemo(
    () => Math.max(...visitorsCountries.map(d => d.value)),
    [visitorsCountries],
  );

  return (
    <Card className="mt-4 flex h-[30rem] w-full flex-col">
      <CardHeader className="px-6 pb-2 pt-4 font-bold">
        Traffic by Country
        <CardDescription className="text-muted-foreground mt-2 flex items-center gap-2 text-sm font-normal">
          <CalendarIcon className="size-4" />
          <span>{visitorsCountriesDateRange}</span>
        </CardDescription>
      </CardHeader>

      <CardContent className="relative flex h-full gap-2 p-2">
        {visitorsCountries.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            No Top Visitors Countries Data Available
          </div>
        ) : (
          <>
            <ComposableMap projectionConfig={{ scale: 150 }} className="h-full w-1/2">
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const countryName = geo.properties.name;
                    const countryCode =
                      getAlpha2Code(countryName, 'en') ?? countryName.toUpperCase();
                    const allCountryNames = getName(countryCode, 'en', { select: 'all' });
                    const countryData = countryCode
                      ? visitorsCountries.find(
                          d => allCountryNames?.includes(d.label) || d.label === countryCode,
                        )
                      : null;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={countryData ? getBlueShade(countryData.value, maxShare) : '#F0F0F0'}
                        stroke={countryData ? getBlueShade(countryData.value, maxShare) : '#D6D6DA'}
                        strokeWidth={countryData ? 1 : 0.5}
                        style={{
                          default: {
                            outline: 'none',
                          },
                          hover: {
                            fill: countryData ? '#1E40AF' : '#D6D6DA',
                            stroke: countryData ? '#1E40AF' : '#D6D6DA',
                            outline: 'none',
                          },
                        }}
                        data-tip={
                          countryData ? `${countryName}: ${countryData.value}%` : countryName || ''
                        }
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            <div className="flex w-1/2 flex-col gap-2 p-4">
              <div className="flex items-center justify-between font-medium">
                <div className="w-1/2">Country</div>
                <div className="w-1/2">Traffic Share</div>
              </div>

              <Separator className="w-full" />

              {visitorsCountries.map(({ label, value }, index) => {
                const countryCode = getAlpha2Code(label, 'en') ?? label.toUpperCase();

                return (
                  <Fragment key={label}>
                    <div className="flex items-center justify-between">
                      <div className="flex w-1/2 items-center gap-2">
                        <CircleFlag countryCode={countryCode.toLowerCase()} className="size-4" />

                        <span className="text-sm text-gray-700">{label}</span>
                      </div>
                      <div className="flex w-1/2 items-center gap-x-4 gap-y-2">
                        <span className="w-10 text-sm text-gray-700">{value}%</span>
                        <div className="h-2 flex-1 overflow-hidden bg-gray-200">
                          <div className="h-full bg-blue-500" style={{ width: `${value}%` }} />
                        </div>
                      </div>
                    </div>
                    {index < visitorsCountries.length - 1 && <Separator className="w-full" />}
                  </Fragment>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
